import { useCurrentAccount } from "@mysten/dapp-kit-react";
import { useEffect, useState } from "react";

import { config } from "@/config";
import { useSuiQueryContext } from "@/context/SUIQuery/useSuiQueryContext";
import { useSuiOwnedObjects } from "@/hooks/sui/queries";

import { OwnedLicensesProps } from "./OwnedLicenses.types";

// MIGRATION: JSON-RPC's `filter: { MatchAll: [{ StructType }] }` becomes a plain
// `type` string on the core API (only one type predicate is supported, which is
// all this query ever used).
const LICENSE_TYPE = `${config.packageId}::swarm_license_sale::License`;

export const OwnedLicenses: React.FC<OwnedLicensesProps> = () => {
  const [nextCursor, setNextCursor] = useState<string | null | undefined>(undefined);

  const account = useCurrentAccount();

  const { setOwnedLicenses, transactionOutput } = useSuiQueryContext();

  const { data, isPending, error, refetch, isFetched } = useSuiOwnedObjects({
    owner: account?.address,
    type: LICENSE_TYPE,
    cursor: nextCursor,
  });

  useEffect(() => {
    if (!data?.objects) return undefined;

    const count = data.objects.length;

    // MIGRATION: contents come back as flat JSON — `price.current`, not
    // `content.fields.price.fields.current`.
    const licensePrice = (license: (typeof data.objects)[number]) =>
      (license.json as unknown as { price: { current: string } } | null)?.price.current;

    const airdropsCount = data.objects.filter((license) => licensePrice(license) === "0").length;

    const salesCount = data.objects.filter((license) => licensePrice(license) !== "0").length;

    if (data.hasNextPage && data.cursor) {
      setNextCursor(data.cursor);
    }

    setOwnedLicenses({
      count,
      airdropsCount,
      salesCount,
      refetch,
    });
  }, [data?.objects]);

  useEffect(() => {
    if (!isFetched) return;

    refetch();
  }, [account?.address, isFetched, transactionOutput.purchaseLicense?.onSuccess?.digest]);

  if (error) {
    return null;
  }

  if (isPending) {
    return null;
  }

  return null;
};
